import {useMemo} from 'react'
import {UnderlineNav, PageLayout} from '@primer/react'
import {BookIcon, LawIcon, ChecklistIcon, LogIcon} from '@primer/octicons-react'
import type {GettingStarted, ModelInputSchema, ShowModelPayload} from '../../../types'
import type {Model} from '@github-ui/marketplace-common'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {clsx} from 'clsx'
import {ModelOverviewHeader} from './ModelOverviewHeader'
import {GiveFeedback} from '../../../components/GiveFeedback'
import {MiniPlayground} from './MiniPlayground'
import SidebarInfo from '../../../components/ModelDetailsSidebar/SidebarInfo'
import {LanguagesSection} from '../../../components/ModelDetailsSidebar/LanguagesSection'
import {TagsSection} from '../../../components/ModelDetailsSidebar/TagsSection'
import {Link} from '@github-ui/react-router'
import {userHasAccessToModel} from '../../../utils/model-access'
import styles from './ModelLayout.module.css'
import responsiveStyles from '../../../components/responsive.module.css'

const tabs = {
  readme: {
    name: 'README',
    icon: BookIcon,
  },
  transparency: {
    name: 'Transparency',
    icon: LogIcon,
  },
  evaluation: {
    name: 'Evaluation',
    icon: ChecklistIcon,
  },
  license: {
    name: 'License',
    icon: LawIcon,
  },
}

export type ModelLayoutTab = keyof typeof tabs

const noEvaluationReport = 'This model does not provide an evaluation report.'
const noTransparencyNotes = 'The model does not have specific notes.'
const noAdditionalTransparencyNotes = 'This model does not provide any additional notes.'

export function ModelLayout({
  activeTab,
  children,
  model,
  modelInputSchema,
  gettingStarted,
}: React.PropsWithChildren<{
  activeTab: ModelLayoutTab
  model: Model
  modelInputSchema: ModelInputSchema
  gettingStarted: GettingStarted
}>) {
  const {modelLicense, restrictedModels} = useRoutePayload<ShowModelPayload>()
  const {tags, task, evaluation, notes, supported_languages} = model
  const labels = {tags, task}
  const languages = supported_languages.filter(language => language !== null)
  const showEvaluation = evaluation && noEvaluationReport !== evaluation
  const showTransparency = notes && ![noTransparencyNotes, noAdditionalTransparencyNotes].includes(notes)

  const canUseModel = useMemo(() => {
    if (task !== 'chat-completion') return false
    return userHasAccessToModel(model, restrictedModels)
  }, [task, model, restrictedModels])

  const getNavItem = (tab: ModelLayoutTab) => {
    const {name: tabName, icon: Icon} = tabs[tab]
    return (
      <UnderlineNav.Item
        leadingVisual={<Icon />}
        as={Link}
        to={`?tab=${tab}`}
        aria-current={activeTab === tab ? 'page' : undefined}
        tabIndex={0}
      >
        {tabName}
      </UnderlineNav.Item>
    )
  }

  return (
    <div className="d-flex flex-column">
      <GiveFeedback mobile className={responsiveStyles.hideOnRegular} />
      <PageLayout columnGap="normal" padding="normal" className={styles.noPaddingOnNarrow}>
        <PageLayout.Header>
          <ModelOverviewHeader model={model} gettingStarted={gettingStarted} canUseModel={canUseModel} />
        </PageLayout.Header>
        <PageLayout.Content as="div" className={clsx('p-0', styles.contentPaddingOnNarrow)}>
          {canUseModel && <MiniPlayground model={model} modelInputSchema={modelInputSchema} />}
          <div className="border rounded-2">
            <UnderlineNav aria-label="Model navigation">
              {getNavItem('readme')}
              {showEvaluation && getNavItem('evaluation')}
              {showTransparency && getNavItem('transparency')}
              {modelLicense && getNavItem('license')}
            </UnderlineNav>
            <div className="p-2">{children}</div>
          </div>
          <div className={clsx('d-flex flex-column gap-3 tmp-pt-3 tmp-pb-5', responsiveStyles.hideOnRegular)}>
            {(labels.tags.length > 0 || labels.task) && <TagsSection labels={labels} />}
            {languages.length > 0 && <LanguagesSection languages={languages} />}
          </div>
        </PageLayout.Content>
        <PageLayout.Pane hidden={{narrow: true}}>
          <SidebarInfo model={model} />
        </PageLayout.Pane>
      </PageLayout>
    </div>
  )
}
